import isTruthy from "@/lib/utils/boolean/isTruthy";
import { RunWithAssociations } from "@shared/lib/database/queries/fetchRunWithAssociations";
import { parseISO } from "date-fns";
import { computed, Ref } from "vue";
import useRunStatusPlayers from "./useRunStatusPlayers";

export default function (run: Ref<RunWithAssociations | null>) {
  const {
    statusPlayers,
    inPlayers,
    maybePlayers,
    outPlayers,
  } = useRunStatusPlayers(run);

  const statusPlayerIds = computed(() => {
    return statusPlayers.value.map(({
      id,
    }) => id);
  });

  const waitlistPlayers = computed(() => {
    return (run.value?.sub_requests || [])
      .filter((subRequest) => {
        return !subRequest.accepted_at
          && subRequest.probability > 0
          && !statusPlayerIds.value.includes(subRequest.player_id);
      })
      .sort(sortByCreated)
      .map((subRequest) => subRequest.player)
      .filter(isTruthy);
  });

  const approvedWaitlistPlayers = computed(() => {
    return (run.value?.sub_requests || [])
      .filter((subRequest) => {
        return !!subRequest.accepted_at
          && !statusPlayerIds.value.includes(subRequest.player_id);
      })
      .sort(sortByCreated)
      .map((subRequest) => subRequest.player)
      .filter(isTruthy);
  });

  const crewPlayers = computed(() => {
    const crewPlayerMap = new Map(run.value?.crews.flatMap((crew) => crew.players).map((player) => [player.id, player]) || []);

    return Array.from(crewPlayerMap.values());
  });

  const expectedPlayers = computed(() => {
    return [...(run.value?.season?.players || crewPlayers.value), ...approvedWaitlistPlayers.value].sort((a, b) => a.name.localeCompare(b.name));
  });

  const pendingPlayers = computed(() => {
    const statusPlayerIds = run.value?.statuses.map((status) => status.player_id) || [];

    return expectedPlayers.value.filter((player) => !statusPlayerIds.includes(player.id));
  });

  function sortByCreated (a: { created_at: string }, b: { created_at: string }) {
    return +parseISO(a.created_at) - +parseISO(b.created_at);
  }

  return {
    inPlayers,
    maybePlayers,
    outPlayers,
    waitlistPlayers,
    approvedWaitlistPlayers,
    pendingPlayers,
    crewPlayers,
  };
}
